import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';

import { fetchMerchantIsWoltPlus } from 'apis/merchant-gateway/merchant-analytics/api';
import { Merchant } from 'apis/restaurant-api/merchant-admin/merchant/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import Loading from 'core/Loading';
import { useIsMerchantV2ApiEnabled } from 'core/hooks/useIsMerchantV2ApiEnabled';
import { useCanViewInsights, MessagePage } from 'modules/experience-core';
import { useExperienceModuleContext } from 'modules/experience-core';
import { useSelectedEntityVenues } from 'modules/experience-core';
import { ExperienceEntityType } from 'modules/experience-core';

import InsightRoutes from '../InsightRoutes';
import useInsightV2WoltPlusHidden from '../hooks/useInsightV2WoltPlusHidden';
import { InsightEntity } from '../types';

const ExperienceInsights = () => {
  const { experienceEntity, selectedExperienceEntity, country, isWoltPlus } = useExperienceModuleContext();

  const venueExperienceEntities = useSelectedEntityVenues();
  const venues: Venue[] = useMemo(() => {
    return experienceEntity.experienceEntityType === ExperienceEntityType.VENUE
      ? [experienceEntity.experienceEntityObject]
      : venueExperienceEntities.map(venue => venue.experienceEntityObject);
  }, [experienceEntity, venueExperienceEntities]);

  const { isWoltPlusHidden, isLoading: isLoadingWoltPlusHidden } = useInsightV2WoltPlusHidden(country);

  // The Wolt+ information is not a part of the merchant api V2 payload
  const merchantId =
    experienceEntity.experienceEntityType === ExperienceEntityType.MERCHANT
      ? experienceEntity.experienceEntityId
      : undefined;
  const { isMerchantV2ApiEnabled, isLoadingIsMerchantV2ApiEnabled } = useIsMerchantV2ApiEnabled(!merchantId);
  // Do not do the Wolt+ api call unless we know it is needed
  const isFetchWoltPlusEnabled =
    !!merchantId &&
    !isWoltPlusHidden &&
    !isLoadingWoltPlusHidden &&
    !isLoadingIsMerchantV2ApiEnabled &&
    isMerchantV2ApiEnabled;

  const { data: merchantIsWoltPlus, isLoading: isLoadingMerchantIsWoltPlus } = useQuery(
    ['/merchant-analytics-api/merchants/', merchantId, '/wolt-plus'],
    () => {
      if (!merchantId) {
        return;
      }
      return fetchMerchantIsWoltPlus(merchantId);
    },
    {
      enabled: isFetchWoltPlusEnabled,
      refetchOnWindowFocus: false,
    },
  );
  const isWoltPlusInsights = isFetchWoltPlusEnabled && isMerchantV2ApiEnabled ? !!merchantIsWoltPlus : isWoltPlus;

  const insightEntity: InsightEntity = useMemo(
    () => ({
      type: selectedExperienceEntity.experienceEntityType as any,
      id: selectedExperienceEntity.experienceEntityId,
      isWoltPlus: !isWoltPlusHidden && isWoltPlusInsights,
      venue: venues[0],
      venues,
      merchant: selectedExperienceEntity.experienceEntityObject as Merchant,
      country,
    }),
    [selectedExperienceEntity, venues, isWoltPlusInsights, isWoltPlusHidden, country],
  );

  const canViewInsights = useCanViewInsights(selectedExperienceEntity);

  if (insightEntity.type === ExperienceEntityType.VENUE && insightEntity.venue.type === 'daas') {
    return <MessagePage variant="not_supported" />;
  }

  if (
    isLoadingWoltPlusHidden ||
    isLoadingIsMerchantV2ApiEnabled ||
    (isFetchWoltPlusEnabled && isLoadingMerchantIsWoltPlus)
  ) {
    return <Loading />;
  }

  return canViewInsights ? (
    <InsightRoutes isExperienceFlow insightEntity={insightEntity} />
  ) : (
    <MessagePage variant="missing_permissions" />
  );
};

export default ExperienceInsights;
